import React, { useEffect, useRef, useState } from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, usePreloadedQuery, type EntryPointProps } from 'react-relay';
import { CatchAndFilterExpectedRelayErrorsBoundary } from '@atlassian/jira-project-create-common/src/common/expected-network-error-boundary/index.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { UNSAFE_noExposureExp, expValEquals } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import {
	useActiveRightSidebarState,
	useRightSidebarController,
} from '@atlassian/jira-layout-controller/src/controllers/layout-controller/consumers/right-sidebar-controller/index.tsx';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { src_projectsDirectoryTemplatesSidebar_RecommendationsInProjectListQuery } from '@atlassian/jira-relay/src/__generated__/src_projectsDirectoryTemplatesSidebar_RecommendationsInProjectListQuery.graphql';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import {
	PACKAGE_NAME,
	PANEL_ID,
	SIDEBAR_WIDTH,
	SIDEBAR_WIDTH_REFRESH,
	TEAM_NAME,
} from './constants.tsx';
import RecommendationsInProjectListContainer from './ui/index.tsx';

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export { PANEL_ID, SIDEBAR_WIDTH } from './constants.tsx';
// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export { useSetProjectListRightPanelState } from './services/set-right-panel-state/index.tsx';

type Queries = {
	templatesQueryRef: src_projectsDirectoryTemplatesSidebar_RecommendationsInProjectListQuery;
};

type Props = EntryPointProps<Queries, {}, { sidebarRefreshEnabled: boolean }, {}>;

const JiraProjectsTemplateListRightPanel = ({
	queries: { templatesQueryRef },
	props: { sidebarRefreshEnabled },
}: Props) => {
	const { openRightSidebar, closeRightSidebar } = useRightSidebarController();

	const rootQueryRef =
		usePreloadedQuery<src_projectsDirectoryTemplatesSidebar_RecommendationsInProjectListQuery>(
			// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
			graphql`
				query src_projectsDirectoryTemplatesSidebar_RecommendationsInProjectListQuery(
					$cloudId: ID!
					$isAnonymous: Boolean!
					$isAllowingEndUsersToAccessCustomTemplates: Boolean!
				) @preloadable {
					jira @skip(if: $isAnonymous) {
						userPreferences(cloudId: $cloudId) @optIn(to: "JiraUserPreferences") {
							projectListRightPanelState @optIn(to: "JiraProjectListRightPanelState")
						}
						sidebarExperimentTemplates: projectListViewTemplates(
							cloudId: $cloudId
							experimentKey: "sidebarExperiment"
						) @optIn(to: "JiraProjectListViewTemplate") {
							totalCount
						}
						canCreateProject: canPerform(type: CREATE_PROJECT, cloudId: $cloudId)
							@optIn(to: "JiraAction")
						shouldDisplay: canPerform(type: VIEW_PROJECT_CREATION_ENTRY, cloudId: $cloudId)
							@optIn(to: "JiraAction")
							@include(if: $isAllowingEndUsersToAccessCustomTemplates)

						...ui_projectsDirectoryTemplatesSidebar_RecommendationsInProjectList
					}
				}
			`,
			templatesQueryRef,
		);

	const { jira } = rootQueryRef;

	const isAnonymous = templatesQueryRef?.variables.isAnonymous;
	const shouldDisplayCreateProject = fg('allow_end_users_to_access_custom_templates')
		? jira?.shouldDisplay
		: jira?.canCreateProject;

	const { createAnalyticsEvent } = useAnalyticsEvents();
	useEffect(() => {
		if (isAnonymous || !jira) return;
		if (
			!(typeof jira?.sidebarExperimentTemplates?.totalCount === 'number') ||
			!(jira?.sidebarExperimentTemplates?.totalCount > 0) ||
			!jira?.userPreferences?.projectListRightPanelState ||
			!shouldDisplayCreateProject
		) {
			fireTrackAnalytics(createAnalyticsEvent({}), 'sidebarEligibility failed');
			return;
		}

		if (jira?.userPreferences?.projectListRightPanelState === 'OPEN') {
			openRightSidebar(
				PANEL_ID,
				sidebarRefreshEnabled ? SIDEBAR_WIDTH_REFRESH : SIDEBAR_WIDTH,
				false,
				true,
			);
		}

		if (jira?.userPreferences?.projectListRightPanelState === 'CLOSED') {
			closeRightSidebar(PANEL_ID);
		}
	}, [
		openRightSidebar,
		closeRightSidebar,
		createAnalyticsEvent,
		isAnonymous,
		jira?.sidebarExperimentTemplates?.totalCount,
		jira?.userPreferences?.projectListRightPanelState,
		shouldDisplayCreateProject,
		jira,
		sidebarRefreshEnabled,
	]);

	const rightSidebarCurrentState = useActiveRightSidebarState();
	const [localPanelId, setLocalPanelId] = useState<string | undefined>();
	const prevPanelIdRef = useRef<string | undefined>();
	const delayRef = useRef<NodeJS.Timeout | undefined>(undefined);
	const panelId = rightSidebarCurrentState?.panelId;

	/**
	 * This use effect is used to add (when is necessary) a delay
	 * in the panelId => localPanelId syncing
	 */
	useEffect(() => {
		clearTimeout(delayRef.current);

		if (prevPanelIdRef.current === PANEL_ID) {
			/**
			 * The right-sidebar is showing the templates panel...
			 */
			if (
				rightSidebarCurrentState &&
				panelId !== undefined &&
				!rightSidebarCurrentState.isCollapsed &&
				!rightSidebarCurrentState.isMinimised
			) {
				/**
				 * and is open, not collapsed and we want to show a panel
				 * with an ID !== PANEL_ID
				 */
				setLocalPanelId(panelId);
			} else {
				/**
				 * and we want to close it
				 */
				delayRef.current = setTimeout(() => {
					setLocalPanelId(panelId);
				}, 310);
			}
		} else {
			/**
			 * If the right sidebar is not showing the templates panel,
			 * we just keep localPanelId in sync with panelId
			 */
			setLocalPanelId(panelId);
		}

		prevPanelIdRef.current = panelId;

		return () => clearTimeout(delayRef.current);
	}, [panelId, rightSidebarCurrentState]);

	useEffect(
		() => () => {
			closeRightSidebar(PANEL_ID);
		},
		[closeRightSidebar],
	);

	if (localPanelId !== PANEL_ID) {
		return null;
	}

	if (isAnonymous) return null;

	if (!rootQueryRef?.jira) {
		throw new Error('Failed to load sidebar content');
	}

	return (
		<RecommendationsInProjectListContainer
			refreshEnabled={expValEquals(
				'grw-1532_jsm_jpd_project_directory_sidebar_refresh',
				'cohort',
				'variation',
			)}
			rootQueryRef={rootQueryRef.jira}
		/>
	);
};

const JiraProjectsTemplateListRightPanelContainerOld = (props: Props) => {
	const [sidebarRefreshConfig] = UNSAFE_noExposureExp(
		'grw-1532_jsm_jpd_project_directory_sidebar_refresh',
	);
	const sidebarRefreshEnabled = sidebarRefreshConfig.get('cohort', 'not-enrolled') === 'variation';

	return (
		<JSErrorBoundary
			fallback="unmount"
			id="right-sidebar-layout-component"
			packageName={PACKAGE_NAME}
			teamName={TEAM_NAME}
			attributes={{ sidebarVisualRefreshEnabled: sidebarRefreshEnabled }}
		>
			<JiraProjectsTemplateListRightPanel
				{...props}
				props={{ ...props.props, sidebarRefreshEnabled }}
			/>
		</JSErrorBoundary>
	);
};
const JiraProjectsTemplateListRightPanelContainerNew = (props: Props) => {
	const [sidebarRefreshConfig] = UNSAFE_noExposureExp(
		'grw-1532_jsm_jpd_project_directory_sidebar_refresh',
	);
	const sidebarRefreshEnabled = sidebarRefreshConfig.get('cohort', 'not-enrolled') === 'variation';

	return (
		<JSErrorBoundary
			fallback="unmount"
			id="right-sidebar-layout-component"
			packageName={PACKAGE_NAME}
			teamName={TEAM_NAME}
			attributes={{ sidebarVisualRefreshEnabled: sidebarRefreshEnabled }}
		>
			<CatchAndFilterExpectedRelayErrorsBoundary>
				<JiraProjectsTemplateListRightPanel
					{...props}
					props={{ ...props.props, sidebarRefreshEnabled }}
				/>
			</CatchAndFilterExpectedRelayErrorsBoundary>
		</JSErrorBoundary>
	);
};

const JiraProjectsTemplateListRightPanelContainer = componentWithCondition(
	() => fg('hix-6900-relay-errors-for-project-recommendations'),
	JiraProjectsTemplateListRightPanelContainerNew,
	JiraProjectsTemplateListRightPanelContainerOld,
);

export default JiraProjectsTemplateListRightPanelContainer;
